import { useEffect, useState } from 'react';
import { Loader2, Coins, Sparkles, XCircle, Megaphone } from 'lucide-react';
import { admin, ApiError } from '../lib/api.js';
import { PLANS } from '../lib/config.js';
import { useSession } from '../lib/session.jsx';
import { Glass, Heading, Button, Tag, Empty } from './ui.jsx';

/**
 * Keeper tools: grant tokens, grant or end a promotional subscription.
 *
 * Rendering this panel is a convenience, not a permission: every RPC it calls
 * re-checks profiles.role on the server and refuses anyone else. A user who
 * forces it to appear gets 42501 back on every button.
 */
export default function KeeperTools() {
  const { user, isAdmin, refresh } = useSession();
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState(200);
  const [tier, setTier] = useState('premium');
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [aff, setAff] = useState(null);          // affiliate row of the selected member, or null
  const [affForm, setAffForm] = useState({ code: '', commissionPct: 25, welcomeTokens: 20 });
  const [payoutReport, setPayoutReport] = useState(null);

  const load = () => admin.users().then((rows) => {
    setUsers(rows);
    setTarget((t) => t ?? rows.find((r) => r.id === user?.id)?.id ?? rows[0]?.id ?? null);
  }).catch((e) => setMsg({ tone: 'rust', text: e.message }));

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);
  useEffect(() => {
    if (!isAdmin || !target) return;
    admin.affiliateOf(target).then((a) => {
      setAff(a ?? null);
      setAffForm({ code: a?.code ?? '', commissionPct: a ? Number(a.commission_pct) : 25, welcomeTokens: a?.welcome_tokens ?? 20 });
    }).catch(() => setAff(null));
  }, [isAdmin, target]);
  if (!isAdmin) return null;

  const run = async (key, fn, ok) => {
    setBusy(key); setMsg(null);
    try {
      await fn();
      await load();
      if (target === user?.id) await refresh();
      setMsg({ tone: 'sage', text: ok });
    } catch (e) {
      setMsg({ tone: 'rust', text: e instanceof ApiError && e.isForbidden ? 'The server refused: not an admin.' : e.message });
    } finally { setBusy(null); }
  };

  const row = users.find((u) => u.id === target);
  const active = row?.sub_status === 'active' && row.current_period_end && new Date(row.current_period_end) > new Date();

  return (
    <Glass className="rise rise-6" style={{ borderColor: 'color-mix(in oklab, var(--gold) 45%, transparent)' }}>
      <Heading eyebrow="Keeper tools" title="Grants"
        action={<Tag tone="building">Admin only</Tag>} />
      <p className="text-sm -mt-2 mb-5" style={{ color: 'var(--text-2)' }}>
        Free tokens and promotional plans, written to the ledger under your name. The server checks your role
        on every call; this panel grants nothing by itself.
      </p>

      <label className="block space-y-2 mb-5">
        <span className="eyebrow">Member</span>
        <select className="input" value={target ?? ''} onChange={(e) => setTarget(e.target.value)}>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.email}{u.id === user?.id ? ' (you)' : ''} · {u.token_balance} tokens · {u.tier ?? 'no plan'}
            </option>
          ))}
        </select>
        {row && (
          <span className="block text-xs" style={{ color: 'var(--text-3)' }}>
            {row.full_name ?? '—'} · {row.role}
            {active ? ` · ${PLANS[row.tier]?.name} until ${new Date(row.current_period_end).toLocaleDateString()}` : ' · no active plan'}
          </span>
        )}
      </label>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="card-flat p-4 space-y-3">
          <p className="font-semibold text-sm flex items-center gap-2"><Coins size={14} style={{ color: 'var(--gold)' }} /> Tokens</p>
          <div className="flex gap-2">
            {[50, 200, 500].map((n) => (
              <button key={n} onClick={() => setAmount(n)} className="chip px-3 py-1.5 text-xs font-semibold" data-on={amount === n}>{n}</button>
            ))}
            <input type="number" min={1} max={100000} value={amount} onChange={(e) => setAmount(Number(e.target.value))}
                   className="input !py-1.5 !px-3 !w-24 text-sm" aria-label="Amount" />
          </div>
          <Button variant="gold" size="sm" disabled={!target || busy || amount < 1}
                  onClick={() => run('tokens', () => admin.grantTokens(target, amount, 'keeper grant'), `Granted ${amount} tokens.`)}>
            {busy === 'tokens' ? <Loader2 size={13} className="animate-spin" /> : <Coins size={13} />} Grant {amount}
          </Button>
        </div>

        <div className="card-flat p-4 space-y-3">
          <p className="font-semibold text-sm flex items-center gap-2"><Sparkles size={14} style={{ color: 'var(--sage)' }} /> Plan</p>
          <div className="flex flex-wrap gap-2">
            {Object.values(PLANS).map((p) => (
              <button key={p.tier} onClick={() => setTier(p.tier)} className="chip px-3 py-1.5 text-xs font-semibold" data-on={tier === p.tier}>{p.name}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-2)' }}>
            for
            <input type="number" min={1} max={366} value={days} onChange={(e) => setDays(Number(e.target.value))}
                   className="input !py-1.5 !px-3 !w-20 text-sm" aria-label="Days" />
            days · grants the plan's monthly tokens
          </div>
          <div className="flex gap-2">
            <Button variant="sage" size="sm" disabled={!target || busy}
                    onClick={() => run('plan', () => admin.grantSubscription(target, tier, days, 'keeper grant'), `${PLANS[tier].name} granted for ${days} days.`)}>
              {busy === 'plan' ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Grant plan
            </Button>
            {active && (
              <Button variant="quiet" size="sm" disabled={busy}
                      onClick={() => run('end', () => admin.endSubscription(target), 'Plan ended.')}>
                <XCircle size={13} /> End plan
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="card-flat p-4 space-y-3 mt-4">
        <p className="font-semibold text-sm flex items-center gap-2">
          <Megaphone size={14} style={{ color: 'var(--clay)' }} /> Creator affiliate
          {aff && <Tag tone={aff.enabled ? 'active' : 'expired'} dot>{aff.enabled ? 'Active' : 'Revoked'}</Tag>}
        </p>
        <p className="text-xs" style={{ color: 'var(--text-2)' }}>
          Creators get an invite code; their followers get welcome tokens and the creator earns a share of what they pay.
        </p>
        <div className="grid grid-cols-3 gap-2">
          <label className="block space-y-1"><span className="eyebrow">Code</span>
            <input className="input !py-1.5 !px-3 text-sm" placeholder="auto" value={affForm.code} maxLength={12}
                   onChange={(e) => setAffForm({ ...affForm, code: e.target.value.toUpperCase() })} /></label>
          <label className="block space-y-1"><span className="eyebrow">Share %</span>
            <input type="number" min={0} max={100} className="input !py-1.5 !px-3 text-sm" value={affForm.commissionPct}
                   onChange={(e) => setAffForm({ ...affForm, commissionPct: Number(e.target.value) })} /></label>
          <label className="block space-y-1"><span className="eyebrow">Welcome tokens</span>
            <input type="number" min={0} max={1000} className="input !py-1.5 !px-3 text-sm" value={affForm.welcomeTokens}
                   onChange={(e) => setAffForm({ ...affForm, welcomeTokens: Number(e.target.value) })} /></label>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="primary" size="sm" disabled={!target || busy}
                  onClick={() => run('aff', async () => {
                    const a = await admin.setAffiliate(target, { enabled: true, commissionPct: affForm.commissionPct,
                      welcomeTokens: affForm.welcomeTokens, code: affForm.code || null });
                    setAff(a); setAffForm((f) => ({ ...f, code: a.code }));
                  }, aff ? 'Creator settings updated.' : 'Creator status granted.')}>
            {busy === 'aff' ? <Loader2 size={13} className="animate-spin" /> : <Megaphone size={13} />}
            {aff?.enabled ? 'Update' : aff ? 'Re-enable' : 'Make creator'}
          </Button>
          {aff?.enabled && (
            <Button variant="quiet" size="sm" disabled={busy}
                    onClick={() => run('affoff', async () => setAff(await admin.setAffiliate(target, { enabled: false })), 'Creator status revoked.')}>
              <XCircle size={13} /> Revoke
            </Button>
          )}
        </div>
      </div>

      <div className="card-flat p-4 space-y-3 mt-4">
        <p className="font-semibold text-sm flex items-center gap-2"><Coins size={14} style={{ color: 'var(--gold)' }} /> Creator payouts</p>
        <p className="text-xs" style={{ color: 'var(--text-2)' }}>
          Runs automatically on the 1st of each month. Preview shows who would be paid now; Pay now transfers through Stripe.
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button variant="glass" size="sm" disabled={busy}
                  onClick={() => run('payprev', async () => setPayoutReport(await admin.runPayouts({ dryRun: true })), 'Preview ready.')}>
            {busy === 'payprev' ? <Loader2 size={13} className="animate-spin" /> : <Coins size={13} />} Preview
          </Button>
          <Button variant="gold" size="sm" disabled={busy}
                  onClick={() => { if (window.confirm('Transfer all payable creator balances through Stripe now?'))
                    run('paynow', async () => setPayoutReport(await admin.runPayouts({ dryRun: false })), 'Payout round finished.'); }}>
            {busy === 'paynow' ? <Loader2 size={13} className="animate-spin" /> : <Coins size={13} />} Pay now
          </Button>
        </div>
        {payoutReport && (
          <ul className="text-xs space-y-1" style={{ color: 'var(--text-2)' }}>
            {payoutReport.results.length === 0 && <li>Nobody is owed ${(payoutReport.minimum_cents / 100).toFixed(0)} or more right now.</li>}
            {payoutReport.results.map((r, i) => (
              <li key={i} className="flex justify-between gap-3">
                <span>{r.affiliate} · {r.status}{r.reason ? ` (${r.reason})` : ''}{r.error ? ` — ${r.error}` : ''}</span>
                <span className="nums">${(r.amount_cents / 100).toFixed(2)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {msg && <p className="text-sm mt-4" style={{ color: `var(--${msg.tone})` }}>{msg.text}</p>}
      {users.length === 0 && !msg && <Empty className="mt-4">Loading members…</Empty>}
    </Glass>
  );
}
